import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderService } from 'src/app/order.service';
import { NgNavigatorShareService } from 'ng-navigator-share';
import { environment } from 'src/environments/environment';
import { Store } from 'src/app/models/store';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MultiStoreLocationComponent } from '../multi-store-location/multi-store-location.component';
import { AddGiftCardDialogComponent } from './add-gift-card-dialog/add-gift-card-dialog.component';
import { GiftCardBarcodeDialogComponent } from './gift-card-barcode-dialog/gift-card-barcode-dialog.component';
import { UnlinkGiftCardDialogComponent } from './unlink-gift-card-dialog/unlink-gift-card-dialog.component';
import { RefillGiftCardDialogComponent } from './refill-gift-card-dialog/refill-gift-card-dialog.component';
import { LinkedGiftCard } from 'src/app/models/gift-card';
import { IMqttMessage, MqttService } from 'ngx-mqtt';
import { Subscription } from 'rxjs';
import { CardDesign, GiftDesignService, StoreGiftDesign, designFor } from 'src/app/gift-design/gift-design.service';


@Component({
  selector: 'app-my-offers',
  templateUrl: './my-offers.component.html',
  styleUrls: ['./my-offers.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyOffersComponent implements OnInit, OnDestroy {


  constructor(
    private route: ActivatedRoute,
    private orderService:OrderService,
    private router: Router,
    ngNavigatorShareService: NgNavigatorShareService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private mqttService: MqttService,
    private cdr: ChangeDetectorRef,
    private giftDesigns: GiftDesignService,
  ) {
    this.ngNavigatorShareService = ngNavigatorShareService;
   }

  /** Card faces the store designed (null keeps the old look). */
  giftDesign: StoreGiftDesign | null = null;
  private faces = new Map<string, CardDesign | null>();

  /** The face for a program's card, or the store default for physical cards and store credit. */
  faceFor(offerId: number | null | undefined): CardDesign | null {
    const key = String(offerId ?? '');
    if (!this.faces.has(key)) {
      this.faces.set(key, designFor(this.giftDesign, offerId ?? null));
    }
    return this.faces.get(key);
  }

  private ngNavigatorShareService: NgNavigatorShareService;
  private mqttSub: Subscription;

  phoneNumber;
  otp;
  otpSent;
  otpSending = false;
  otpVerifying = false;
  phone_number_verified = false;
  myOffers;
  sentGifts = [];
  isLoading = true;
  hash;
  storeId;
  store;
  linkedGiftCards: LinkedGiftCard[] = [];

  ngOnInit() {
    this.storeId = this.route.snapshot.paramMap.get('storeId');
    this.hash = this.route.snapshot.paramMap.get('hash');
    if (this.storeId) {
      this.giftDesigns.forStore(this.storeId).subscribe(d => {
        this.giftDesign = d;
        this.faces.clear();
        this.cdr.markForCheck();
      });
      this.orderService.getStoreInfo(this.storeId).subscribe(store => {
        this.store = Object.assign(new Store(), store);
        localStorage.setItem('currentStore', this.store.id);
        this.cdr.markForCheck();
      })
    }


    if(this.hash){
      this.orderService.getMyOffersWithHash(this.storeId, this.hash).subscribe((response: any) => {
        if (response && response.my_offers) {
          this.myOffers = response.my_offers;
          this.sentGifts = response.sent_gifts || [];
        } else {
          this.myOffers = response;
          this.sentGifts = [];
        }
        this.phone_number_verified = true;
        this.isLoading = false;
        this.loadLinkedGiftCards();
        this.cdr.markForCheck();
      })
    }else{
      if(localStorage.getItem('phoneNumber') !== null){
        this.phone_number_verified = true;
        this.phoneNumber = localStorage.getItem('phoneNumber')
      }

      if(this.phone_number_verified){
        this.orderService.getMyOffers(this.phoneNumber).subscribe((response: any) => {
          if (response && response.my_offers) {
            this.myOffers = response.my_offers;
            this.sentGifts = response.sent_gifts || [];
          } else {
            this.myOffers = response;
            this.sentGifts = [];
          }
          this.isLoading = false;
          this.cdr.markForCheck();
        })
        this.loadLinkedGiftCards();
      }
    }
  }

  loadLinkedGiftCards() {
    const phoneNumber = this.phoneNumber || localStorage.getItem('phoneNumber');
    const storeId = localStorage.getItem('currentStore');

    if (phoneNumber && storeId) {
      this.orderService.getLinkedGiftCards(phoneNumber, storeId).subscribe(
        (response: any) => {
          this.linkedGiftCards = response.data || [];
          this.cdr.markForCheck();
        },
        (error) => {
          this.cdr.markForCheck();
        }
      );
    }
  }

  openAddGiftCardDialog() {
    const dialogRef = this.dialog.open(AddGiftCardDialogComponent, {
      panelClass: 'full-screen-modal'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadLinkedGiftCards();
      }
    });
  }

  openBarcodeDialog(card: LinkedGiftCard) {
    const dialogRef = this.dialog.open(GiftCardBarcodeDialogComponent, {
      data: {
        card: card
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(updatedCard => {
      if (updatedCard) {
        this.loadLinkedGiftCards();
      }
    });
  }

  openUnlinkDialog(card: LinkedGiftCard) {
    const storeId = localStorage.getItem('currentStore');
    const dialogRef = this.dialog.open(UnlinkGiftCardDialogComponent, {
      data: {
        card: card,
        storeId: storeId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Gift card removed from wallet', 'OK', {
          duration: 3000,
          verticalPosition: 'top'
        });
        this.loadLinkedGiftCards();
      }
    });
  }

  openRefillDialog(card: LinkedGiftCard) {
    const storeId = localStorage.getItem('currentStore');
    const phoneNumber = this.phoneNumber || localStorage.getItem('phoneNumber');
    const dialogRef = this.dialog.open(RefillGiftCardDialogComponent, {
      panelClass: 'full-screen-modal',
      data: {
        card: card,
        storeId: storeId,
        phoneNumber: phoneNumber,
        paymentGateway: this.store?.payment_gateway,
        store: this.store
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadLinkedGiftCards();
      }
    });
  }

  openDigitalCardBarcodeDialog(offer: any) {
    const dialogRef = this.dialog.open(GiftCardBarcodeDialogComponent, {
      data: {
        card: {
          card_number: offer.card_number,
          barcode_image: offer.barcode_image,
          balance: offer.balance
        }
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(updatedCard => {
      if (updatedCard) {
        offer.balance = updatedCard.balance;
        this.cdr.markForCheck();
      }
    });
  }

  getMaskedCardNumber(cardNumber: string): string {
    if (cardNumber && cardNumber.length >= 4) {
      return '****' + cardNumber.slice(-4);
    }
    return cardNumber || '';
  }


  sendOTP(){
    if(this.otpSending) return;
    this.otpSending = true;
    this.orderService.otpForUser(this.getPlainPhoneNumber()).subscribe(() => {
      this.otpSent = true;
      this.otpSending = false;
      this.cdr.markForCheck();
    }, () => {
      this.otpSending = false;
      this.cdr.markForCheck();
    })
  }

  verifyOTP(){
    if (this.otpVerifying) return;
    this.otpVerifying = true;
    this.orderService.verifyOtpForUser(this.getPlainPhoneNumber(), this.otp).subscribe({
      next: data => {
        this.otpVerifying = false;
        this.phone_number_verified = data['is_verified'];
        localStorage.setItem("phoneNumber", data['phone_number']);
        localStorage.setItem("phoneNumberVerified", data['is_verified']);
        if(data['is_verified']) {
          this.orderService.getMyOffers(this.getPlainPhoneNumber()).subscribe(myOffers => {
            this.myOffers = myOffers;
            this.isLoading = false;
            this.cdr.markForCheck();
          });
          this.loadLinkedGiftCards();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.otpVerifying = false;
        this.cdr.markForCheck();
      }
    });
  }

  changePhone(){
    this.phone_number_verified = false;
    this.otpSent = false;
    this.otpSending = false;
    this.otpVerifying = false;
  }

  getPlainPhoneNumber(){
    if(this.phoneNumber){
      let ph = this.phoneNumber.replace(/[\s()-]/g, '')
      return '+1' + ph.substring(0,10)
    }
    return "";
  }

  getPlainPhoneNumberFromString(phoneNumber:string){
    let ph = phoneNumber.replace(/[\s()-]/g, '')
    return '+1' + ph.substring(0,10)
  }

  getFormattedPhoneNumber(phoneNumber){
    return phoneNumber.replace(/^(\+1)(\d{0,3})(\d{0,3})(\d{0,4})/, '($2) $3-$4')
  }

  redeem(offer){
    if (offer.offer && offer.offer.corporate) {
      this.orderService.accountStoreList(offer.offer.corporate).subscribe((data: any) => {
        const dialogRef = this.dialog.open(MultiStoreLocationComponent, {
          maxWidth: '390px',
          maxHeight: '600px',
          disableClose: true,
          data: {
            store_list: data.data
          }
        });
    
        dialogRef.afterClosed().subscribe(result => {
          this.orderService.getStoreInfo(result).subscribe(store => {
            this.store = Object.assign(new Store(), store);
            localStorage.setItem('currentStore', result);
            this.router.navigate([this.hash, 'my-offers', offer.id, 'redeem']);

          })
        });
      });
    } else {
      const storeId = localStorage.getItem('currentStore');
      if (offer.offer.store != storeId) {
        localStorage.setItem('currentStore', offer.offer.store);
      }
      this.router.navigate([this.hash, 'my-offers', offer.id, 'redeem']);
    }
  }

  subscribeToBalanceUpdates(hash: string) {
    const topic = `/consumer/${hash}/gift_card_update`;
    this.mqttSub = this.mqttService.observe(topic, { qos: 1 } as any)
      .subscribe((message: IMqttMessage) => {
        this.refreshGiftCards();
      });
  }

  refreshGiftCards() {
    if (this.hash) {
      this.orderService.getMyOffersWithHash(this.storeId, this.hash).subscribe((response: any) => {
        if (response && response.my_offers) {
          this.myOffers = response.my_offers;
          this.sentGifts = response.sent_gifts || [];
        } else {
          this.myOffers = response;
        }
        this.cdr.markForCheck();
      });
      this.loadLinkedGiftCards();
    } else if (this.phoneNumber) {
      this.orderService.getMyOffers(this.phoneNumber).subscribe((response: any) => {
        if (response && response.my_offers) {
          this.myOffers = response.my_offers;
          this.sentGifts = response.sent_gifts || [];
        } else {
          this.myOffers = response;
          this.sentGifts = [];
        }
        this.cdr.markForCheck();
      });
      this.loadLinkedGiftCards();
    }
  }

  ngOnDestroy() {
    if (this.mqttSub) {
      this.mqttSub.unsubscribe();
    }
  }

  goBack(){
    window.history.back()
  }

  copyMessage(val: string){
    navigator.clipboard.writeText(val).then(() => {
      this.snackBar.open('Link copied to clipboard - share link with the new owner!', "OK", {
        duration: 5000,
        verticalPosition: 'top'
      });
    });
  }

  confirm(offer){
    // ENG-5034: route to the send-gift flow (text delivery + claim tracking) instead of the raw share sheet
    if (this.storeId && this.hash) {
      this.router.navigate(['store', this.storeId, this.hash, 'send-gift']);
      return;
    }

    let url = `${environment.hostName}/gift/${offer.offer_order.offer_order_id}/`
    if (!this.ngNavigatorShareService.canShare()) {
      this.copyMessage(url);
      return;
    }

    if(confirm('Once you gift this card and it’s claimed, you will no longer be able to use this card!')){
      this.ngNavigatorShareService.share({
        title: 'I got this for you!',
        text: 'I got this for you!\n\n',
        url: url
      }).then( (response) => {
      })
      .catch( (error) => {
      });
    }
  }
}
